import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ChevronDown, TableProperties } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { withYear } from "@/lib/year";
import LeafCard from "@/components/monev/LeafCard";
import NilaiCard from "@/components/monev/NilaiCard";
import RekapHasil from "@/components/monev/RekapHasil";

export default function MonevModule({ user, year = 2026 }) {
  const isAdmin = user?.role === "ppid";
  const isPts = user?.key === "pts";
  const [tpl, setTpl] = useState(null);
  const [subs, setSubs] = useState({});
  const [nilai, setNilai] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [view, setView] = useState("all");
  const [isianOpen, setIsianOpen] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);
  const [showRekap, setShowRekap] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());
  const yearRef = useRef(year);
  yearRef.current = year;
  const tplRef = useRef(null);
  tplRef.current = tpl;

  const load = useCallback(async () => {
    try {
      const y = yearRef.current;
      const reqs = [
        api("/api/monev/template"),
        api(`/api/monev/nilai?year=${y}`),
        api(`/api/monev/submissions?year=${y}`),
      ];
      if (isAdmin) reqs.push(api("/api/admin/accounts"));
      const [t, n, s, acc] = await Promise.all(reqs);
      setTpl(t);
      setNilai(n);
      setSubs(s);
      if (acc) setAccounts(acc);
    } catch (e) {
      toast(formatApiError(e.message));
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // Muat ulang ringan: unggah/hapus isian hanya mengubah submissions & nilai
  const loadLight = useCallback(async (y) => {
    try {
      const yy = y ?? yearRef.current;
      const [n, s] = await Promise.all([
        api(`/api/monev/nilai?year=${yy}`),
        api(`/api/monev/submissions?year=${yy}`),
      ]);
      setNilai(n);
      setSubs(s);
    } catch (e) {
      toast(formatApiError(e.message));
    }
  }, []);

  // Muat ulang data saat tahun diganti dari menu (lewati muatan pertama)
  const firstYear = useRef(true);
  useEffect(() => {
    if (firstYear.current) {
      firstYear.current = false;
      return;
    }
    loadLight();
  }, [year]);

  const setAllRequired = async (val) => {
    setSavingCfg(true);
    try {
      await api("/api/admin/monev/settings", {
        method: "POST",
        body: JSON.stringify({ allRequired: val }),
      });
      await load();
    } catch (e) {
      toast(formatApiError(e.message));
    } finally {
      setSavingCfg(false);
    }
  };

  const toggleGroup = (code) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  // Saat filter "Lihat Isian" aktif, buka semua kelompok yang berisi item
  // pengelola tersebut; kembali ke "Kelola Pengisian" menutup semuanya agar
  // halaman tetap ringkas.
  useEffect(() => {
    const t = tplRef.current;
    if (!t) return;
    if (view === "all") {
      setOpenGroups(new Set());
      return;
    }
    const byCode = Object.fromEntries(t.items.map((it) => [it.code, it]));
    const codes = new Set();
    for (const it of t.items) {
      if (!it.leaf) continue;
      const a = it.assignedTo;
      const visible =
        view === "ppid"
          ? !a ||
            (Array.isArray(a) && (a.length === 0 || a.includes("ppid")))
          : a === "all" || (Array.isArray(a) && a.includes(view));
      if (!visible) continue;
      let cur = it;
      while (cur) {
        if (cur.level === 1) {
          codes.add(cur.code);
          break;
        }
        cur = cur.parent ? byCode[cur.parent] : null;
      }
    }
    setOpenGroups(codes);
  }, [view]);

  if (isPts) {
    return (
      <div data-testid="monev-pts-note">
        <h1 className="font-display text-3xl font-semibold text-[#0B2440] md:text-4xl">
          e-Monev
        </h1>
        <p className="mt-8 rounded-2xl border border-dashed border-[#E3EBF3] bg-white px-6 py-10 text-center text-sm leading-relaxed text-slate-400">
          Modul e-Monev khusus Perguruan Tinggi Swasta sedang dalam
          pengembangan dan akan tersedia pada pembaruan berikutnya.
        </p>
      </div>
    );
  }

  if (!tpl) {
    return (
      <p className="text-sm font-semibold text-slate-500">
        Memuat modul e-Monev...
      </p>
    );
  }

  if (isAdmin && showRekap) {
    return (
      <RekapHasil
        tpl={tpl}
        subs={subs}
        accounts={accounts}
        onBack={() => setShowRekap(false)}
        year={year}
      />
    );
  }

  const labelBy = {};
  for (const a of accounts || []) labelBy[a.key] = a.label;
  const isianLabel = (v) =>
    v === "ppid" ? "PPID (isi sendiri)" : labelBy[v] || v;
  const isianOptions = [
    { key: "ppid", label: "PPID (isi sendiri)" },
    ...(accounts || [])
      .filter((a) => a.key !== "ppid" && a.key !== "pts")
      .map((a) => ({ key: a.key, label: a.label })),
  ];

  const leavesInView = tpl.items.filter((it) => {
    if (!it.leaf) return false;
    const a = it.assignedTo;
    if (isAdmin) {
      if (view === "all") return true;
      if (view === "ppid")
        return (
          !a || (Array.isArray(a) && (a.length === 0 || a.includes("ppid")))
        );
      return a === "all" || (Array.isArray(a) && a.includes(view));
    }
    return (
      user.key !== "pts" &&
      (a === "all" || (Array.isArray(a) && a.includes(user.key)))
    );
  });
  const byCode = Object.fromEntries(tpl.items.map((it) => [it.code, it]));
  const keep = new Set();
  for (const lf of leavesInView) {
    let cur = byCode[lf.code];
    while (cur) {
      keep.add(cur.code);
      cur = cur.parent ? byCode[cur.parent] : null;
    }
  }
  const byParent = {};
  for (const it of tpl.items) {
    if (keep.has(it.code)) {
      const key = it.parent || "";
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(it);
    }
  }
  const nilaiByCode = Object.fromEntries(
    (nilai?.indikator || []).map((x) => [x.code, x])
  );

  const rows = [];
  function collect(code, depth) {
    const kids = byParent[code] || [];
    for (const it of kids) {
      rows.push({ it, depth });
      if (!it.leaf) collect(it.code, depth + 1);
    }
  }
  collect("", 0);

  // Kelompokkan baris per indikator level-1 (A–E) untuk panel yang bisa
  // dibuka-tutup.
  const groups = [];
  for (const { it, depth } of rows) {
    if (it.level === 1) groups.push({ ind: it, body: [] });
    else if (groups.length) groups[groups.length - 1].body.push({ it, depth });
  }

  const renderRow = ({ it, depth }) => {
    if (it.leaf) {
      return (
        <div key={it.code} style={{ paddingLeft: depth * 10 }} className="mb-5">
          <LeafCard
            item={it}
            sub={subs[it.code]}
            allRequired={tpl.allRequired}
            canFill={
              isAdmin ||
              (user.key !== "pts" &&
                (it.assignedTo === "all" ||
                  (Array.isArray(it.assignedTo) &&
                    it.assignedTo.includes(user.key))))
            }
            isAdmin={isAdmin}
            accounts={accounts}
            userKey={user.key}
            year={year}
            onRefresh={load}
            onRefreshLight={loadLight}
          />
        </div>
      );
    }
    if (it.level === 2) {
      return (
        <h3
          key={it.code}
          style={{ paddingLeft: depth * 10 }}
          className="mt-10 font-display text-base font-semibold text-[#0B2440] md:text-lg"
        >
          {withYear(it.title, year)}
        </h3>
      );
    }
    return (
      <h4
        key={it.code}
        style={{ paddingLeft: depth * 10 }}
        className="mt-6 text-sm font-bold text-[#1479BF]"
      >
        {withYear(it.title, year)}
      </h4>
    );
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold text-[#0B2440] md:text-4xl">
        e-Monev {year}
      </h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
        Modul monitoring dan evaluasi keterbukaan informasi publik sesuai
        format penilaian PPID. Klik banner indikator untuk membuka isian.
      </p>

      <div className="mt-8">
        <NilaiCard nilai={nilai} />
      </div>

      {isAdmin && (
        <div
          data-testid="monev-admin-controls"
          className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#E3EBF3] bg-white px-5 py-4"
        >
          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="monev-view-switch"
          >
            <button
              data-testid="monev-view-all"
              onClick={() => setView("all")}
              className={`rounded-full px-4 py-2 text-[11px] font-bold tracking-[0.08em] transition-colors duration-300 ${
                view === "all"
                  ? "bg-[#1479BF] text-white"
                  : "border border-[#E3EBF3] bg-white text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
              }`}
            >
              Kelola Pengisian
            </button>

            <div className="relative" data-testid="monev-isian-dropdown">
              <button
                data-testid="monev-isian-trigger"
                onClick={() => setIsianOpen((o) => !o)}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-bold tracking-[0.08em] transition-colors duration-300 ${
                  view !== "all"
                    ? "bg-[#1479BF] text-white"
                    : "border border-[#E3EBF3] bg-white text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                }`}
              >
                {view !== "all"
                  ? `Lihat Isian: ${isianLabel(view)}`
                  : "Lihat Isian"}
                <ChevronDown
                  aria-hidden="true"
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${
                    isianOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
              {isianOpen && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setIsianOpen(false)}
                  />
                  <div className="absolute left-0 top-full z-50 mt-2 max-h-[70vh] w-72 overflow-y-auto rounded-2xl border border-[#E3EBF3] bg-white p-2 shadow-xl">
                    <p className="px-3 py-2 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                      Filter isian per pengelola
                    </p>
                    {isianOptions.map((opt) => (
                      <button
                        key={opt.key}
                        data-testid={`monev-isian-option-${opt.key}`}
                        onClick={() => {
                          setView(opt.key);
                          setIsianOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-xs font-semibold transition-colors duration-150 ${
                          view === opt.key
                            ? "bg-[#EFF6FC] text-[#1479BF]"
                            : "text-slate-600 hover:bg-[#F3F7FB]"
                        }`}
                      >
                        {opt.label}
                        {view === opt.key && (
                          <Check
                            aria-hidden="true"
                            className="h-3.5 w-3.5 shrink-0"
                          />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <button
              data-testid="monev-rekap-button"
              onClick={() => setShowRekap(true)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0B2440] px-4 py-2 text-[11px] font-bold tracking-[0.08em] text-white transition-colors duration-300 hover:bg-[#1479BF]"
            >
              <TableProperties aria-hidden="true" className="h-3.5 w-3.5" />
              Lihat Rekap Hasil Monev
            </button>
          </div>
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
            <input
              type="checkbox"
              data-testid="monev-all-required"
              checked={tpl.allRequired}
              disabled={savingCfg}
              onChange={(e) => setAllRequired(e.target.checked)}
              className="h-4 w-4 accent-[#1479BF]"
            />
            Semua kolom wajib terisi
          </label>
        </div>
      )}

      <div className="mt-6">
        {leavesInView.length === 0 ? (
          <p
            data-testid="monev-empty-assignment"
            className="mt-8 rounded-2xl border border-dashed border-[#E3EBF3] bg-white px-6 py-10 text-center text-sm leading-relaxed text-slate-400"
          >
            {isAdmin
              ? view === "all"
                ? "Tidak ada item isian pada template e-Monev."
                : `Belum ada item e-Monev untuk ${isianLabel(view)}. Pilih "Kelola Pengisian" untuk menugaskan pengelola.`
              : "Belum ada item e-Monev yang ditugaskan kepada Anda. Hubungi Admin PPID."}
          </p>
        ) : (
          groups.map(({ ind, body }) => {
            const open = openGroups.has(ind.code);
            const nLeaf = body.filter((r) => r.it.leaf).length;
            return (
              <section
                key={ind.code}
                data-testid={`monev-group-${ind.code}`}
                className="mb-6"
              >
                <button
                  type="button"
                  data-testid={`monev-group-toggle-${ind.code}`}
                  onClick={() => toggleGroup(ind.code)}
                  title={open ? "Tutup isian indikator ini" : "Buka isian indikator ini"}
                  className="flex w-full flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#0B2440] px-5 py-4 text-left transition-colors duration-200 hover:bg-[#123255]"
                >
                  <span className="font-display text-base font-semibold leading-snug text-white md:text-lg">
                    {withYear(ind.title, year)}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-white/60">
                      {nLeaf} item isian
                    </span>
                    {nilaiByCode[ind.code] && (
                      <span
                        data-testid={`monev-nilai-${ind.code}`}
                        className="rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-white"
                      >
                        Simulasi: {nilaiByCode[ind.code].skor} / {ind.max}
                      </span>
                    )}
                    <ChevronDown
                      aria-hidden="true"
                      className={`h-4 w-4 shrink-0 text-white/80 transition-transform duration-300 ${
                        open ? "rotate-180" : ""
                      }`}
                    />
                  </span>
                </button>
                {open && (
                  <div
                    data-testid={`monev-group-body-${ind.code}`}
                    className="pt-5"
                  >
                    {body.map(renderRow)}
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}